export const HEADER_CSS = `
  .hd {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 1000;
    transition: background 0.4s ease, backdrop-filter 0.4s ease, box-shadow 0.4s ease;
  }

  /* Top-to-bottom dark scrim so text always readable on hero images */
  .hd--transparent {
    background: linear-gradient(
      to bottom,
      rgba(7,30,48,0.72) 0%,
      rgba(7,30,48,0.28) 70%,
      transparent 100%
    );
    box-shadow: none;
    backdrop-filter: none;
  }

  .hd--solid {
    background: rgba(7,30,48,0.92);
    backdrop-filter: blur(14px) saturate(1.4);
    -webkit-backdrop-filter: blur(14px) saturate(1.4);
    box-shadow: 0 1px 0 rgba(201,165,106,0.18), 0 4px 32px rgba(7,30,48,0.45);
  }

  .hd__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 2vw;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 72px;
    transition: height 0.35s ease;
  }

  .hd--solid .hd__inner {
    height: 64px;
  }

  /* ── Logo ── */
  .hd__logo {
    display: flex;
    align-items: center;
    text-decoration: none;
    flex-shrink: 0;
  }

  .hd__logo-svg text {
    font-family: var(--font-serif);
  }
  .hd__logo-img {
    height: 44px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  /* ── Nav ── */
  .hd__nav {
    display: flex;
    align-items: center;
    gap: 0;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .hd__nav-item {
    position: relative;
  }

  .hd__nav-link {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 16px;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(245,242,236,0.75);
    text-decoration: none;
    white-space: nowrap;
    transition: color 0.2s;
    cursor: pointer;
    background: none;
    border: none;
    outline: none;
  }

  .hd__nav-link:hover,
  .hd__nav-item--active > .hd__nav-link {
    color: var(--color-ivory);
  }

  .hd__nav-link svg {
    transition: transform 0.22s;
    flex-shrink: 0;
  }

  .hd__nav-item--open > .hd__nav-link svg {
    transform: rotate(180deg);
  }

  /* ── Dropdown ── */
  .hd__dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    min-width: 200px;
    background: rgba(7,30,48,0.97);
    backdrop-filter: blur(12px) saturate(1.4);
    -webkit-backdrop-filter: blur(12px) saturate(1.4);
    border: 1px solid rgba(201,165,106,0.18);
    border-top: 2px solid var(--color-gold);
    list-style: none;
    padding: 8px 0;
    margin: 0;
    opacity: 0;
    visibility: hidden;
    transform: translateX(-50%) translateY(-6px);
    transition: opacity 0.22s, transform 0.22s, visibility 0.22s;
    pointer-events: none;
  }

  /* Invisible bridge fills the gap between nav link and dropdown */
  .hd__dropdown::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 0; right: 0;
    height: 12px;
  }

  .hd__nav-item:hover .hd__dropdown,
  .hd__nav-item--open .hd__dropdown {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
    pointer-events: auto;
  }

  .hd__dropdown-link {
    display: block;
    padding: 11px 20px;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0.04em;
    color: rgba(245,242,236,0.6);
    text-decoration: none;
    transition: color 0.18s, background 0.18s;
    white-space: nowrap;
  }

  .hd__dropdown-link:hover {
    color: var(--color-ivory);
    background: rgba(201,165,106,0.07);
  }

  .hd__dropdown-group {
    display: contents;
  }

  .hd__dropdown-group-label {
    display: block;
    padding: 10px 20px 4px;
    font-family: var(--font-sans);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(201,165,106,0.6);
    margin-top: 4px;
  }

  .hd__dropdown-group:first-child .hd__dropdown-group-label {
    margin-top: 0;
  }

  .hd__dropdown-link--sub {
    padding-left: 20px;
  }

  /* ── CTA button ── */
  .hd__cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    font-family: var(--font-sans);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-ivory);
    border: 1px solid rgba(201,165,106,0.6);
    text-decoration: none;
    transition: background 0.22s, border-color 0.22s, gap 0.22s;
    flex-shrink: 0;
    margin-left: 16px;
  }

  .hd__cta:hover {
    background: rgba(201,165,106,0.12);
    border-color: var(--color-gold);
    gap: 12px;
  }

  /* ── Right side ── */
  .hd__right {
    display: flex;
    align-items: center;
    gap: 0;
  }

  /* ── Hamburger (mobile) ── */
  .hd__hamburger {
    display: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    width: 40px;
    height: 40px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    margin-left: 8px;
  }

  .hd__hamburger-bar {
    display: block;
    width: 22px;
    height: 1.5px;
    background: var(--color-ivory);
    transition: transform 0.28s, opacity 0.28s, width 0.28s;
    transform-origin: center;
  }

  .hd__hamburger--open .hd__hamburger-bar:nth-child(1) {
    transform: translateY(6.5px) rotate(45deg);
  }
  .hd__hamburger--open .hd__hamburger-bar:nth-child(2) {
    opacity: 0;
    width: 0;
  }
  .hd__hamburger--open .hd__hamburger-bar:nth-child(3) {
    transform: translateY(-6.5px) rotate(-45deg);
  }

  /* ── Mobile drawer ── */
  .hd__mobile {
    position: fixed;
    top: 0; right: 0;
    width: min(320px, 88vw);
    height: 100vh;
    background: var(--color-midnight);
    border-left: 1px solid rgba(201,165,106,0.15);
    z-index: 1001;
    transform: translateX(100%);
    transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
    padding: 52px 32px 40px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .hd__mobile--open {
    transform: translateX(0);
  }

  .hd__mobile-close {
    position: absolute;
    top: 20px; right: 20px;
    width: 36px; height: 36px;
    display: flex; align-items: center; justify-content: center;
    background: none;
    border: none;
    color: var(--color-ivory);
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s;
  }

  .hd__mobile-close:hover {
    opacity: 1;
  }

  .hd__mobile-overlay {
    position: fixed;
    inset: 0;
    background: rgba(7,30,48,0.6);
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.32s, visibility 0.32s;
  }

  .hd__mobile-overlay--open {
    opacity: 1;
    visibility: visible;
  }

  .hd__mobile-nav {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .hd__mobile-section {
    padding: 16px 0 4px;
    font-family: var(--font-sans);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--color-gold);
    opacity: 0.7;
  }

  .hd__mobile-link {
    display: block;
    padding: 12px 0;
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 300;
    letter-spacing: 0.03em;
    color: rgba(245,242,236,0.75);
    text-decoration: none;
    border-bottom: 1px solid rgba(201,165,106,0.08);
    transition: color 0.2s, padding-left 0.2s;
  }

  .hd__mobile-link:hover {
    color: var(--color-ivory);
    padding-left: 6px;
  }

  .hd__mobile-link--child {
    padding-left: 16px;
    font-size: 13px;
    font-weight: 300;
    color: rgba(245,242,236,0.55);
    border-bottom-color: rgba(201,165,106,0.05);
  }

  .hd__mobile-link--child-last {
    border-bottom-color: transparent;
  }

  .hd__mobile-link--child::before {
    content: '–';
    margin-right: 8px;
    color: rgba(201,165,106,0.4);
    font-weight: 300;
  }

  .hd__mobile-link--child:hover {
    color: rgba(245,242,236,0.85);
    padding-left: 20px;
  }

  .hd__mobile-section--sub {
    padding: 8px 0 2px 16px;
    font-size: 8px;
    letter-spacing: 0.2em;
    opacity: 0.6;
  }

  .hd__mobile-link--grand {
    padding-left: 15px;
    font-size: 12px;
  }

  .hd__mobile-link--grand::before {
    content: '–';
    margin-right: 8px;
    color: rgba(201,165,106,0.3);
    font-weight: 300;
  }

  .hd__mobile-link--grand:hover {
    padding-left: 15px;
  }

  .hd__mobile-separator {
    height: 1px;
    background: rgba(201,165,106,0.15);
    margin: 8px 0;
  }

  .hd__mobile-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 32px;
    padding: 14px 28px;
    font-family: var(--font-sans);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-ivory);
    border: 1px solid rgba(201,165,106,0.6);
    text-decoration: none;
    transition: background 0.22s, border-color 0.22s;
    text-align: center;
    justify-content: center;
  }

  .hd__mobile-cta:hover {
    background: rgba(201,165,106,0.12);
    border-color: var(--color-gold);
  }

  /* ── Top gold accent line ── */
  .hd__accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(201,165,106,0.7) 30%,
      rgba(201,165,106,0.7) 70%,
      transparent 100%
    );
    opacity: 0;
    transition: opacity 0.35s;
  }

  .hd--solid .hd__accent {
    opacity: 1;
  }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .hd__nav,
    .hd__cta {
      display: none;
    }
    .hd__hamburger {
      display: flex;
    }
  }

  @media (min-width: 961px) {
    .hd__mobile,
    .hd__mobile-overlay {
      display: none;
    }
  }
`
